import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, Pause, ArrowRight, Check, Menu, Image, Users, Video, Film, Clock, Upload, Sparkles, Mail, Volume2, VolumeX, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useAuth } from "@/App";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Hero Before/After Carousel Component - Simplified with buttons
function HeroCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const videoRef = useRef(null);

  const slides = [
    { before: "/images/before-1.jpg", after: "/videos/after-1.mp4", label: "Exterior" },
    { before: "/images/before-2.jpg", after: "/videos/after-2.mp4", label: "Kitchen" },
    { before: "/images/before-3.jpg", after: "/videos/after-3.mp4", label: "Living Room" },
    { before: "/images/before-4.jpg", after: "/videos/after-4.mp4", label: "Bedroom" },
    { before: "/images/before-5.jpg", after: "/videos/after-5.mp4", label: "Bathroom" }
  ];

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Preload images only (skip video preload on mobile)
  useEffect(() => {
    let loadedCount = 0;

    slides.forEach((slide) => {
      const img = new window.Image();
      img.src = slide.before;
      img.onload = () => {
        loadedCount++;
        if (loadedCount >= slides.length) setAllLoaded(true);
      };
      img.onerror = () => {
        loadedCount++;
        if (loadedCount >= slides.length) setAllLoaded(true);
      };
    });

    // Preload videos only on desktop
    if (!isMobile) {
      slides.forEach((slide) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.muted = true;
        video.src = slide.after;
      });
    }

    const timeout = setTimeout(() => setAllLoaded(true), 3000);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  const goToPrev = () => {
    setShowVideo(false);
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToNext = () => {
    setShowVideo(false);
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  };

  const toggleView = () => {
    setShowVideo(!showVideo);
    if (!showVideo && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  };

  if (!allLoaded) {
    return (
      <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden shadow-xl aspect-[16/9] bg-slate-200">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-slate-300 border-t-[#3C59B2] rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Main Display */}
      <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden shadow-xl aspect-[16/9]">
        {!showVideo ? (
          <img 
            src={slides[currentIndex].before}
            alt={`${slides[currentIndex].label} before`}
            className="w-full h-full object-cover"
            data-testid="hero-before-image"
          />
        ) : (
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            autoPlay
            muted
            playsInline
            loop
            preload="none"
            data-testid="hero-after-video"
            onError={() => setShowVideo(false)}
          >
            <source src={slides[currentIndex].after} type="video/mp4" />
          </video>
        )}
        
        {/* Label Badge - hidden on mobile for performance */}
        <div className={`hidden md:block absolute top-4 left-4 ${showVideo ? 'bg-[#3C59B2]' : 'bg-slate-900/80 backdrop-blur-sm'} text-white rounded-full px-5 py-2 text-sm font-semibold uppercase tracking-wide`}>
          {showVideo ? 'After' : 'Before'}
        </div>

        {/* Room Label - hidden on mobile for performance */}
        <div className="hidden md:block absolute top-4 right-4 bg-white/90 backdrop-blur-sm text-slate-700 rounded-full px-4 py-2 text-sm font-medium">
          {slides[currentIndex].label}
        </div>
      </div>

      {/* Controls Below */}
      <div className="flex items-center justify-center gap-4 mt-6">
        {/* Prev Button */}
        <button
          onClick={goToPrev}
          className="w-12 h-12 rounded-full bg-white border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 flex items-center justify-center transition-all"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-5 h-5 text-slate-600" />
        </button>

        {/* Before/After Toggle */}
        <div className="flex bg-slate-100 rounded-full p-1">
          <button
            onClick={() => setShowVideo(false)}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
              !showVideo ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Before
          </button>
          <button
            onClick={() => { setShowVideo(true); if (videoRef.current) { videoRef.current.currentTime = 0; videoRef.current.play().catch(() => {}); }}}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
              showVideo ? 'bg-[#3C59B2] shadow-sm text-white' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            After
          </button>
        </div>

        {/* Next Button */}
        <button
          onClick={goToNext}
          className="w-12 h-12 rounded-full bg-white border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 flex items-center justify-center transition-all"
          aria-label="Next slide"
        >
          <ChevronRight className="w-5 h-5 text-slate-600" />
        </button>
      </div>

      {/* Dots Indicator */}
      <div className="flex justify-center gap-2 mt-4">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => { setCurrentIndex(index); setShowVideo(false); }}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentIndex 
                ? "bg-[#3C59B2] w-6" 
                : "bg-slate-300 hover:bg-slate-400"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

// Portfolio Video Component with Controls and Fullscreen
function PortfolioVideo({ src, poster, label }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasError, setHasError] = useState(false);

  const togglePlay = () => {
    if (videoRef.current && !hasError) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => setHasError(true));
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    const videoEl = videoRef.current;
    const containerEl = containerRef.current;
    if (!videoEl || hasError) return;

    const doc = document;
    const isFs =
      doc.fullscreenElement ||
      // @ts-ignore - iOS Safari
      doc.webkitFullscreenElement;

    if (!isFs) {
      // iOS Safari native fullscreen
      // @ts-ignore - iOS Safari
      if (typeof videoEl.webkitEnterFullscreen === "function") {
        try {
          // @ts-ignore
          videoEl.webkitEnterFullscreen();
          return;
        } catch {
          // ignore
        }
      }
      const target = containerEl || videoEl;
      if (target.requestFullscreen) {
        target.requestFullscreen().catch(() => {});
      }
    } else {
      if (doc.exitFullscreen) {
        doc.exitFullscreen();
      } else {
        // @ts-ignore - iOS Safari
        doc.webkitExitFullscreen?.();
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleVideoError = () => {
    setHasError(true);
  };

  return (
    <div ref={containerRef} className="relative rounded-2xl overflow-hidden shadow-lg group bg-slate-900">
      <video
        ref={videoRef}
        className="w-full aspect-video object-cover"
        loop
        muted
        playsInline
        poster={poster}
        preload="metadata"
        onError={handleVideoError}
      >
        <source src={src} type="video/mp4" />
      </video>
      
      {/* Big Play Button in Center - shown when paused */}
      {!isPlaying && (
        <button
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity"
          aria-label="Play video"
        >
          <div className="w-20 h-20 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
            <Play className="w-8 h-8 text-slate-700 ml-1" />
          </div>
        </button>
      )}
      
      {/* Controls - always visible on mobile when playing, on hover on desktop */}
      <div
        className={`absolute bottom-4 right-4 flex gap-2 transition-opacity ${
          isPlaying
            ? 'opacity-100 md:opacity-0 md:group-hover:opacity-100'
            : 'opacity-0'
        }`}
      >
        <button
          onClick={togglePlay}
          className="bg-white/90 backdrop-blur-sm hover:bg-white rounded-full p-3 transition-all shadow-lg hover:scale-105"
          aria-label={isPlaying ? "Pause video" : "Play video"}
        >
          {isPlaying ? (
            <Pause className="w-5 h-5 text-slate-700" />
          ) : (
            <Play className="w-5 h-5 text-slate-700" />
          )}
        </button>
        <button
          onClick={toggleMute}
          className="bg-white/90 backdrop-blur-sm hover:bg-white rounded-full p-3 transition-all shadow-lg hover:scale-105"
          aria-label={isMuted ? "Unmute video" : "Mute video"}
        >
          {isMuted ? (
            <VolumeX className="w-5 h-5 text-slate-700" />
          ) : (
            <Volume2 className="w-5 h-5 text-slate-700" />
          )}
        </button>
      </div>
      {label && (
        <span className="absolute bottom-4 left-4 text-xs text-white/70 font-medium">{label}</span>
      )}
    </div>
  );
}

// Comparison Table Component
function ComparisonTable({ onGetStarted }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
      {/* Cliptour Column */}
      <motion.div
        className="bg-white border border-slate-200 rounded-3xl p-8 flex flex-col"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-slate-900 mb-1">Cliptour</h3>
          <p className="text-slate-500">Already furnished</p>
        </div>
        
        <div className="space-y-4 flex-grow">
          <div className="flex items-center gap-3 text-slate-900">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
            <span>10 video clips with lifestyle characters</span>
          </div>
          <div className="flex items-center gap-3 text-slate-900">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
            <span>1 full property tour with music</span>
          </div>
          <div className="flex items-center gap-3 text-slate-900">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
            <span>Smooth cinematic camera movements</span>
          </div>
          <div className="flex flex-wrap gap-2 pt-3 mt-3 border-t border-slate-100">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full px-3 py-1">
              <Clock className="w-3 h-3" /> 24h delivery
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full px-3 py-1">
              <Film className="w-3 h-3" /> HD video files
            </span>
          </div>
        </div>

        <Button 
          onClick={() => onGetStarted("cliptour")}
          className="w-full mt-8 bg-slate-900 hover:bg-slate-800 text-white rounded-full py-6"
          data-testid="comparison-cliptour-btn"
        >
          Get Started
        </Button>
      </motion.div>

      {/* Cliptour Staged Column */}
      <motion.div
        className="bg-white border-2 border-[#3C59B2]/20 ring-1 ring-[#3C59B2]/20 rounded-3xl p-8 flex flex-col relative"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
      >
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-[#3C59B2] text-white px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide">
            Most Popular
          </span>
        </div>

        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-slate-900 mb-1">Cliptour Staged</h3>
          <p className="text-slate-500">Empty rooms</p>
        </div>
        
        <div className="space-y-4 flex-grow">
          <div className="flex items-center gap-3 text-slate-900">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
            <span>10 video clips with lifestyle characters</span>
          </div>
          <div className="flex items-center gap-3 text-slate-900">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
            <span>1 full property tour with music</span>
          </div>
          <div className="flex items-center gap-3 text-slate-900">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
            <span>Smooth cinematic camera movements</span>
          </div>
          <div className="flex items-center gap-3 text-[#3C59B2]">
            <span className="w-5 h-5 flex items-center justify-center font-bold text-[#3C59B2] flex-shrink-0">+</span>
            <span className="font-medium">10 photos with virtual furniture & decor</span>
          </div>
          <div className="flex flex-wrap gap-2 pt-3 mt-3 border-t border-slate-100">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full px-3 py-1">
              <Clock className="w-3 h-3" /> 48h delivery
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full px-3 py-1">
              <Film className="w-3 h-3" /> HD video files
            </span>
          </div>
        </div>

        <Button 
          onClick={() => onGetStarted("cliptour_staged")}
          className="w-full mt-8 bg-[#3C59B2] hover:bg-[#2d4a8f] text-white rounded-full py-6"
          data-testid="comparison-cliptour-staged-btn"
        >
          Get Started
        </Button>
      </motion.div>
    </div>
  );
}

export default function HomePage() {
  const [showBanner, setShowBanner] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleGetStarted = (service, isB2B = false) => {
    if (user) {
      navigate("/order", { state: { service, isB2B } });
    } else {
      navigate("/signup", { state: { redirectTo: "/order", service, isB2B } });
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="homepage">
      {/* Promo Banner */}
      {showBanner && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-[#3C59B2] text-white py-2.5" data-testid="promo-banner">
          <div className="container-custom flex items-center justify-center gap-4">
            <span className="text-sm font-medium">
              <Sparkles className="inline w-4 h-4 mr-2" />
              $100 off your first order
            </span>
            <button 
              onClick={() => setShowBanner(false)}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              data-testid="dismiss-banner-btn"
              aria-label="Dismiss banner"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <Header showBanner={showBanner} />

      {/* Hero Section */}
      <section className={`py-12 lg:py-16 ${showBanner ? 'pt-36 lg:pt-44' : 'pt-28 lg:pt-36'}`} data-testid="hero-section">
        <div className="container-custom">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial="initial"
            animate="animate"
            variants={stagger}
          >
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6"
              variants={fadeInUp}
            >
              Luxury video production from photos.{" "}
              <span className="text-[#3C59B2]">No filming required.</span>
            </motion.h1>
            
            <motion.p 
              className="text-base md:text-lg lg:text-xl text-slate-600 mx-auto mb-4 text-center px-4 max-w-3xl"
              variants={fadeInUp}
            >
              Static photos become cinematic. Bring your listings to life and create an immediate emotional connection.
            </motion.p>

            <motion.p 
              className="text-[#3C59B2] font-semibold mb-8"
              variants={fadeInUp}
            >
              AI powered, human storytelling.
            </motion.p>

            <motion.div variants={fadeInUp}>
              <Button 
                onClick={() => handleGetStarted("cliptour_staged")}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-6 text-lg font-semibold transition-all hover:scale-105"
                data-testid="hero-get-started-btn"
              >
                Get Started <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
          </motion.div>

          {/* Hero Carousel */}
          <motion.div 
            className="mt-16"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <HeroCarousel />
          </motion.div>
        </div>
      </section>

      {/* Problem/Solution */}
      <section className="py-12 lg:py-16 bg-slate-50" data-testid="problem-solution-section">
        <div className="container-custom">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xl lg:text-2xl text-slate-700 leading-relaxed">
              <strong className="text-slate-900">Cliptour creates cinematic property videos using only your photos — an effortless way to elevate your listings and connect with buyers emotionally. </strong>
            </p>
          </motion.div>
        </div>
      </section>

      {/* What You Get - Portfolio Videos */}
      <section className="py-12 lg:py-16" data-testid="deliverables-section">
        <div className="container-custom">
          <motion.h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
    See our work
          </motion.h2>

          <div className="space-y-8">
            <PortfolioVideo 
              src="https://pub-f769c896ded6405a9b5fd7301d2d91a2.r2.dev/portfolio-1.mp4" 
              poster="/images/poster-1.jpg"
              label="Cliptour Staged"
            />
            <PortfolioVideo 
              src="https://pub-f769c896ded6405a9b5fd7301d2d91a2.r2.dev/portfolio-2.mp4" 
              poster="/images/poster-2.jpg"
              label="Cliptour Staged"
            />
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-12 lg:py-16 bg-slate-50" data-testid="how-it-works-section">
        <div className="container-custom">
          <motion.h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            How it works
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {[
              { step: "1", icon: Upload, title: "Upload your photos", desc: "Send all property photos — we'll select the best 10 for staging and animation" },
              { step: "2", icon: Sparkles, title: "We work our magic", desc: "We select, stage, animate & edit your photos into cinematic video" },
              { step: "3", icon: Video, title: "Receive your video", desc: "Get your clips and montage in 24-48 business hours, ready to post" }
            ].map((item, i) => (
              <motion.div
                key={item.step}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
              >
                <div className="w-16 h-16 bg-white border-2 border-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                  <item.icon className="w-7 h-7 text-slate-700" />
                </div>
                <div className="text-sm font-semibold text-[#3C59B2] mb-2">Step {item.step}</div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12 lg:py-16" id="pricing" data-testid="pricing-section">
        <div className="container-custom">
          <motion.h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Simple, transparent pricing
          </motion.h2>
          <p className="text-center text-slate-600 mb-16">2 revisions included. $50 per additional revision.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Cliptour */}
            <motion.div
              className="card-base flex flex-col"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Cliptour</h3>
                <p className="text-slate-600">Already furnished?</p>
              </div>
              
              <div className="mb-6">
                <span className="text-slate-400 line-through text-lg">$450</span>
                <span className="text-4xl font-bold text-slate-900 ml-2">$350</span>
                <span className="text-slate-600 text-sm ml-1">first order</span>
              </div>

              <ul className="space-y-3 mb-6 flex-grow">
                {["10 video clips with lifestyle characters", "1 full property tour with music", "Smooth cinematic camera movements"].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-700">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap gap-2 mb-4">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full px-3 py-1">
                  <Clock className="w-3 h-3" /> 24h delivery
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full px-3 py-1">
                  <Film className="w-3 h-3" /> HD video files
                </span>
              </div>
              
              <Button 
                onClick={() => handleGetStarted("cliptour")}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full py-6"
                data-testid="cliptour-get-started-btn"
              >
                Get Started
              </Button>
            </motion.div>

            {/* Cliptour Staged */}
            <motion.div
              className="card-base flex flex-col relative border-[#3C59B2]/20 ring-1 ring-[#3C59B2]/20"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="absolute -top-3 right-6">
                <span className="badge-popular">Most Popular</span>
              </div>

              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Cliptour Staged</h3>
                <p className="text-slate-600">Empty rooms?</p>
              </div>
              
              <div className="mb-6">
                <span className="text-slate-400 line-through text-lg">$700</span>
                <span className="text-4xl font-bold text-slate-900 ml-2">$600</span>
                <span className="text-slate-600 text-sm ml-1">first order</span>
              </div>

              <ul className="space-y-3 mb-4 flex-grow">
                {["10 video clips with lifestyle characters", "1 full property tour with music", "Smooth cinematic camera movements"].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-700">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
                <li className="flex items-start gap-3 text-[#3C59B2]">
                  <span className="w-5 h-5 flex items-center justify-center font-bold text-[#3C59B2] flex-shrink-0 mt-0.5">+</span>
                  <span className="font-medium">10 photos with virtual furniture & decor</span>
                </li>
              </ul>

              <div className="flex flex-wrap gap-2 mb-4">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full px-3 py-1">
                  <Clock className="w-3 h-3" /> 48h delivery
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full px-3 py-1">
                  <Film className="w-3 h-3" /> HD video files
                </span>
              </div>
              
              <Button 
                onClick={() => handleGetStarted("cliptour_staged")}
                className="w-full bg-[#3C59B2] hover:bg-[#2d4a8f] text-white rounded-full py-6"
                data-testid="cliptour-staged-get-started-btn"
              >
                Get Started
              </Button>
            </motion.div>
          </div>

          <p className="text-center text-slate-600 mt-8">
            5+ properties? <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="text-[#3C59B2] font-medium hover:underline">Contact us for volume pricing</Link>
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 lg:py-16" id="faq" data-testid="faq-section">
        <div className="container-custom">
          <motion.h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Frequently asked questions
          </motion.h2>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {[
                { q: "How does it work?", a: "Simply upload all your property photos through our platform. We'll select the best 10 for staging (if needed) and animation. Cliptour delivers in 24 business hours, Cliptour Staged in 48 business hours. You'll receive 10 individual clips ready for social media, plus one full cinematic montage with music." },
                { q: "What photos do I need to provide?", a: "Send us all your property photos — interior and exterior. We recommend at least 15-20 photos so we can select the best 10 that will create the most compelling story. Higher resolution photos (ideally from a professional camera) work best." },
                { q: "How do you select which photos to use?", a: "Our team of creative directors reviews all submitted photos and selects the 10 that will create the most visually compelling and emotionally engaging story. We prioritize variety (living spaces, bedrooms, kitchen, exterior) and visual impact." },
                { q: "How long does delivery take?", a: "Cliptour (furnished properties) delivers in 24 business hours. Cliptour Staged (empty properties requiring virtual staging) delivers in 48 business hours. Rush delivery options may be available — contact us for details." },
                { q: "What's included in the delivery?", a: "You'll receive: 10 video clips with lifestyle characters (perfect for Instagram Reels, TikTok, Stories), 1 full property tour with music (ideal for YouTube, MLS, and Zillow), all in HD MP4 format (1080p 60fps, 16:9). Cliptour Staged also includes 10 photos with virtual furniture & decor." },
                { q: "Can I request revisions?", a: "Yes! 2 revisions are included with every order. Additional revisions are $50 each. Most clients are thrilled with the first delivery." },
                { q: "Do you offer volume pricing?", a: "Absolutely. If you have 5+ properties or need ongoing production, contact us for custom volume pricing. We also offer special rates for photographers and agencies through our B2B program." }
              ].map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-slate-200 rounded-xl px-6 data-[state=open]:border-[#3C59B2]/30">
                  <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline py-5">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-5">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 lg:py-16 relative overflow-hidden" data-testid="final-cta-section">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=80"
            alt="Luxury home"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/80" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-2xl mx-auto text-center text-white">
            <motion.h2 
              className="text-3xl lg:text-5xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Ready to bring your listings to life?
            </motion.h2>
            <motion.p 
              className="text-slate-300 mb-8 text-lg"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              Transform your property photos into cinematic marketing videos.
            </motion.p>
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Button 
                onClick={() => handleGetStarted("cliptour_staged")}
                className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-semibold"
                data-testid="cta-get-started-btn"
              >
                Get Started
              </Button>
              <Link to="/contact" onClick={() => window.scrollTo(0, 0)}>
                <Button 
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
                  data-testid="cta-contact-btn"
                >
                  Contact Us
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}